/**
 * TransactionDocumentsPage - Page for managing transaction documents
 *
 * Displays the DocumentsTab for a specific transaction.
 */

import { ArrowLeft, FileText } from 'lucide-react';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DocumentsTab } from '../components/documents/DocumentsTab';

export default function TransactionDocumentsPage() {
  const { id } = useParams<{ id: string }>();

  const handleGoBack = useCallback(() => {
    window.history.back();
  }, []);

  if (!id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Invalid Transaction</h2>
          <p className="text-muted-foreground mb-4">No transaction ID provided</p>
          <Link to="/dashboard/drafts">
            <Button>Back to Drafts</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleGoBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">Transaction</h1>
            <p className="text-gray-600">Manage closing disclosure documents</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs defaultValue="documents" className="mb-6">
          <TabsList>
            <TabsTrigger value="preview" asChild>
              <Link to={`/cd/${id}/preview`}>
                <FileText className="h-4 w-4 mr-2" />
                Preview
              </Link>
            </TabsTrigger>
            <TabsTrigger value="documents" asChild>
              <Link to={`/cd/${id}/documents`} replace>
                <FileText className="h-4 w-4 mr-2" />
                Documents
              </Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Documents Tab */}
        <div className="bg-white rounded-lg shadow p-6">
          <DocumentsTab transactionId={id} />
        </div>
      </div>
    </div>
  );
}
